import { deleteData, getData, post } from 'services/api';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { camelizeKeys } from 'humps';

import { IdParamsType } from 'utils/types';
import { ResponsesData, SortField, SortOrder } from 'features/CustomFieldsResponses/types';
import { TicketTypeProps } from 'features/Dashboard/components/punchup/TicketTypesSection/types';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { throwIfRefused } from './apiError';
import { useToastMutation } from './useToastMutation';
import { TICKET_STATE_INVALIDATE_KEYS } from './ticketStateKeys';

/** One entry in a ticket's transaction history (GET /tickets/:id/history). */
export interface TicketHistoryEntry {
	Transaction: {
		ID: number;
		transaction_type: string;
		promo_amount_applied?: number;
		CreatedAt: string;
		[key: string]: unknown;
	};
	TransactionUser: {
		full_name: string;
		email: string;
		[key: string]: unknown;
	};
	UsedPromotion?: unknown;
	[key: string]: unknown;
}

/** Detail fields for a single ticket (GET /tickets/:id). */
export interface TicketDetails {
	ticket_type_id?: number;
	validated?: boolean;
	validated_times?: number;
	/** The ticket's current transaction id — drives the "switch entire transaction" flow.
	 * GET /tickets/:id exposes `last_transaction_id` (there is no separate `transaction_id`). */
	last_transaction_id?: number;
	[key: string]: unknown;
}

export const useEventCustomFields = (isAttendeesList: boolean, isLinkedEventsGroup = false) => {
	const { id } = useParams<IdParamsType>();

	const [searchText, setSearchText] = useState('');
	const [pageParam, setPageParam] = useState(1);
	const [resultsPerPage, setResultsPerPage] = useState(30);
	const [sortBy, setSortBy] = useState<SortField | null>(null);
	const [order, setOrder] = useState<SortOrder>('asc');

	// The festival-wide list is opt-in (`grouped=true`, TIC-732): asked only for a grouped event
	// (`event.scannerGroupId`, read by the page) while "All linked shows" is selected.
	const [showLinkedEvents, setShowLinkedEvents] = useState(true);
	const requestLinkedEvents = isLinkedEventsGroup && showLinkedEvents;

	const responsesQueryObject = useInfiniteQuery({
		queryKey: ['responses', id, searchText, isAttendeesList, sortBy, order, requestLinkedEvents],
		queryFn: ({ pageParam = 1 }): Promise<ResponsesData[]> => {
			let url = `events/${id}/custom-fields/responses?page=${pageParam}&per_page=${resultsPerPage}&search=${searchText}&all_attendees=${isAttendeesList}`;

			if (requestLinkedEvents) {
				url += '&grouped=true';
			}

			if (sortBy) {
				url += `&sort_by=${sortBy}&order=${order}`;
			}

			return getData(url);
		},
		getNextPageParam: (lastPage, pages) => (lastPage.length >= resultsPerPage ? pages.length + 1 : undefined),
		enabled: !!id,
	});

	const handleSort = (field: SortField) => {
		if (sortBy === field) {
			// Toggle order if same field
			setOrder(order === 'asc' ? 'desc' : 'asc');
		} else {
			// New field, default to ascending
			setSortBy(field);
			setOrder('asc');
		}
	};

	return {
		searchText,
		setSearchText,
		pageParam,
		setPageParam,
		resultsPerPage,
		setResultsPerPage,
		responsesQueryObject,
		sortBy,
		order,
		handleSort,
		showLinkedEvents,
		setShowLinkedEvents,
	};
};

export const useResendEmail = (options?: { onSuccess?: () => void }) => {
	const { mutate: resendEmail, isLoading } = useToastMutation<number>({
		mutationFn: (orderId) => post(`/public/ticketing/transactions/${orderId}/send-email`),
		successMessage: 'Email sent successfully',
		errorMessage: 'The email could not be sent. Please check the ticket status and try again.',
		invalidateKeys: [['responses']],
		onSuccess: options?.onSuccess,
	});

	return { resendEmail, isLoading };
};

export const useTransferTicket = (options?: { onSuccess?: () => void }) => {
	const { mutate: transferTicket, isLoading } = useToastMutation<{
		ticketId: number;
		recipientEmail: string;
		recipientFirstName?: string;
		recipientLastName?: string;
	}>({
		mutationFn: ({ ticketId, recipientEmail, recipientFirstName, recipientLastName }) =>
			post('/tickets/admin-transfer', {
				ticket_ids: [ticketId],
				recipient_email: recipientEmail,
				...(recipientFirstName ? { recipient_first_name: recipientFirstName } : {}),
				...(recipientLastName ? { recipient_last_name: recipientLastName } : {}),
			}),
		successMessage: 'Ticket transferred successfully',
		errorMessage: 'The ticket could not be transferred. Please check the email address and ticket status.',
		invalidateKeys: TICKET_STATE_INVALIDATE_KEYS,
		onSuccess: options?.onSuccess,
	});

	return { transferTicket, isLoading };
};

export const useTicketHistory = (ticketId?: number) =>
	useQuery<TicketHistoryEntry[]>({
		queryKey: ['ticket-history', ticketId],
		queryFn: () => (ticketId ? getData(`/tickets/${ticketId}/history`) : Promise.resolve([])),
		enabled: !!ticketId,
	});

export const useTicketDetails = (ticketId?: number) =>
	useQuery<TicketDetails | null>({
		queryKey: ['ticket-details', ticketId],
		queryFn: () => (ticketId ? getData(`/tickets/${ticketId}`) : Promise.resolve(null)),
		enabled: !!ticketId,
	});

/**
 * Resolves a single TicketTypeProps by filtering the camelized event ticket-type list.
 * See AttendeeActions/CLAUDE.md for why the list endpoint (not GET /ticket_types/:id) is canonical.
 */
export const useTicketTypeFromList = (ticketTypeId?: number) => {
	const { id: eventId } = useParams<IdParamsType>();

	return useQuery({
		queryKey: ['ticketTypes', eventId],
		queryFn: (): Promise<any[]> => getData(`/public/ticketing/ticket_types?event_id=${eventId}`),
		select: (data): TicketTypeProps | undefined => {
			const camels = camelizeKeys(data) as unknown as TicketTypeProps[];
			return camels.find((tt) => tt.iD === ticketTypeId);
		},
		enabled: !!eventId && !!ticketTypeId,
	});
};

export const useCancelTicket = (options?: { onSuccess?: () => void }) => {
	const { mutate: cancelTicket, isLoading } = useToastMutation<number>({
		mutationFn: (ticketId) => deleteData(`/tickets/cancel-ticket/${ticketId}`),
		successMessage: 'Ticket canceled successfully',
		errorMessage: 'The ticket could not be canceled. Please check the ticket status.',
		invalidateKeys: TICKET_STATE_INVALIDATE_KEYS,
		onSuccess: options?.onSuccess,
	});
	return { cancelTicket, isLoading };
};

export const useUncancelTicket = (options?: { onSuccess?: () => void }) => {
	const { mutate: uncancelTicket, isLoading } = useToastMutation<number>({
		mutationFn: (ticketId) => post(`/tickets/uncancel-ticket/${ticketId}`),
		successMessage: 'Ticket un-canceled successfully',
		errorMessage: 'The ticket could not be un-canceled. Please check the ticket status.',
		invalidateKeys: TICKET_STATE_INVALIDATE_KEYS,
		onSuccess: options?.onSuccess,
	});
	return { uncancelTicket, isLoading };
};

/** A refusal arrives in the 207 body's `errors[]`; `throwIfRefused` routes it to the error toast. */
export const useRefundTicket = (options?: { onSuccess?: () => void }) => {
	const { mutate: refundTicket, isLoading } = useToastMutation<{
		ticketId: number;
		amount: number;
		description?: string;
	}>({
		mutationFn: ({ ticketId, amount, description }) =>
			post('/tickets/refund', {
				tickets: [{ id: ticketId, amount }],
				description,
			}).then(throwIfRefused),
		successMessage: 'Ticket refunded successfully',
		errorMessage: 'The ticket could not be refunded. Please check the ticket status and amount.',
		invalidateKeys: TICKET_STATE_INVALIDATE_KEYS,
		onSuccess: options?.onSuccess,
	});
	return { refundTicket, isLoading };
};

/**
 * Voids an imported ticket (TIC-952): Punchup never collected its money, so the API refuses a refund and the
 * void is the only way to take it off the door — `POST /tickets/:id/void-external` (admin/editor), which leaves
 * it `External Canceled` (Un-Cancel restores it). A refusal is a plain 4xx `{ error }`, toasted verbatim.
 */
export const useVoidExternalTicket = (options?: { onSuccess?: () => void }) => {
	const { mutate: voidExternalTicket, isLoading } = useToastMutation<{ ticketId: number; reason: string }>({
		mutationFn: ({ ticketId, reason }) => post(`/tickets/${ticketId}/void-external`, { reason }),
		successMessage: 'Ticket voided successfully',
		errorMessage: 'The ticket could not be voided. Please check the ticket status.',
		invalidateKeys: TICKET_STATE_INVALIDATE_KEYS,
		onSuccess: options?.onSuccess,
	});
	return { voidExternalTicket, isLoading };
};

/**
 * Manual ticket validation (door check-in by ticket id) from an attendee row: `POST /tickets/validate`, a 207
 * whose refusals arrive in the body's `errors[]`, so `throwIfRefused` routes them to the error toast.
 * Payload mirrors the working green Scanner caller (`validateTicketsManually`): `ticket_ids`/`event_id` as
 * STRINGS + numeric `time_slot_id: 0` (the backend `Validation` struct binds those types; see
 * `features/PunchupPages/Scanner/CLAUDE.md` invariant 6).
 */
export const useValidateTicket = (options?: { onSuccess?: () => void }) => {
	const { mutate: validateTicket, isLoading } = useToastMutation<{ ticketId: number; eventId: string | number }>({
		mutationFn: ({ ticketId, eventId }) =>
			post('/tickets/validate', {
				ticket_ids: [String(ticketId)],
				event_id: String(eventId),
				time_slot_id: 0,
			}).then(throwIfRefused),
		successMessage: 'Ticket validated successfully',
		errorMessage: 'The ticket could not be validated. Please check the ticket status.',
		invalidateKeys: TICKET_STATE_INVALIDATE_KEYS,
		onSuccess: options?.onSuccess,
	});
	return { validateTicket, isLoading };
};

/**
 * Clears a check-in so the ticket scans again. Admin/editor only: gate the caller on `canUndoCheckIn`, since any
 * other role gets a 401 that the api client reads as an expired session. For a pass, `eventId` is the day whose
 * check-in is cleared; the API answers 409 when the ticket is not checked in for that event.
 */
export const useUndoCheckIn = (options?: { onSuccess?: () => void }) => {
	const { mutate: undoCheckIn, isLoading } = useToastMutation<{ ticketId: number; eventId: string | number }>({
		mutationFn: ({ ticketId, eventId }) => post(`/tickets/${ticketId}/undo-check-in`, { event_id: Number(eventId) }),
		successMessage: 'Check-in undone. The ticket can be scanned again.',
		errorMessage: 'The check-in could not be undone.',
		invalidateKeys: TICKET_STATE_INVALIDATE_KEYS,
		onSuccess: options?.onSuccess,
	});
	return { undoCheckIn, isLoading };
};
